import React from "react";
import {
  Box,
  Button,
  Popover,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { Investigation, Stage } from "../../lusca-search-api/generated-src";
import {
  InvestigationApiError,
  useFollowInvestigation,
} from "../../hooks/useInvestigations";
import { saveErrorText } from "./versionConflict";

interface InvestigationFollowButtonProps {
  stage: Stage;
  investigation: Investigation;
  currentUser?: string | null;
  onError: (message: string) => void;
}

/** "N following" with the followers listed in a popover; nothing when nobody follows. */
export function InvestigationFollowers({
  followers,
  currentUser,
}: {
  followers?: string[];
  currentUser?: string | null;
}) {
  if (!followers?.length) {
    return null;
  }
  return (
    <Popover
      triggerType="text"
      size="small"
      header="Following"
      content={
        <SpaceBetween size="xxs">
          {followers.map((alias) => (
            <Box key={alias}>
              {alias}
              {alias === currentUser ? " (you)" : ""}
            </Box>
          ))}
        </SpaceBetween>
      }
    >
      {followers.length} following
    </Popover>
  );
}

/**
 * Follow / Following toggle in the case header, plus who follows. Followers are told (bell,
 * email, Slack) about comments, status and owner changes and redrive outcomes. The toggle is
 * hidden for the owner, who is already notified about their own case; the count is shown to all.
 */
export function InvestigationFollowButton({
  stage,
  investigation,
  currentUser,
  onError,
}: InvestigationFollowButtonProps) {
  const follow = useFollowInvestigation(stage);
  if (!currentUser) {
    return null;
  }
  const following = !!investigation.followers?.includes(currentUser);
  const followers = (
    <InvestigationFollowers
      followers={investigation.followers}
      currentUser={currentUser}
    />
  );
  if (investigation.owner === currentUser) {
    return followers;
  }
  return (
    <SpaceBetween direction="horizontal" size="xs" alignItems="center">
      {followers}
      <Button
        iconName={following ? "check" : "notification"}
        loading={follow.isLoading}
        ariaLabel={following ? "Unfollow this investigation" : undefined}
        onClick={() => {
          follow
            .mutateAsync({
              investigationId: investigation.id,
              follow: !following,
            })
            .catch((err: InvestigationApiError) => onError(saveErrorText(err)));
        }}
      >
        {following ? "Following" : "Follow"}
      </Button>
    </SpaceBetween>
  );
}
